import {
  ColorlibConnector,
  ColorlibStepIconRoot
} from "@components/CustomSteps";
import { UserContext } from "@context/User/UserContext";
import { getLngCode } from "@i18n/i18n";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SummarizeIcon from "@mui/icons-material/Summarize";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  Box,
  DialogActions,
  IconButton,
  LinearProgress,
  Modal,
  Stack,
  Step,
  StepIconProps,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useChatSummary } from "@services/chatManagerApi";
import { useQueryClient } from "@tanstack/react-query";
import jsPDF from "jspdf";
import React, { useContext, useEffect, useMemo } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4
};

const ColorlibStepIcon = (props: StepIconProps) => {
  const { t } = useTranslation();

  const { active, completed, className } = props;

  const icons: {
    [index: string]: { icon: React.ReactElement<unknown>; tooltip: string };
  } = {
    1: {
      icon: <SwapHorizIcon />,
      tooltip: t("chatbot.video.generation.steps.generating_summary")
    },
    2: {
      icon: <SummarizeIcon />,
      tooltip: t("chatbot.summary.steps.summary_download")
    }
  };

  return (
    <Tooltip title={active ? icons[String(props.icon)].tooltip : ""}>
      <ColorlibStepIconRoot
        ownerState={{ completed, active }}
        className={className}
      >
        {icons[String(props.icon)].icon}
      </ColorlibStepIconRoot>
    </Tooltip>
  );
};

export interface ModalSummaryProps {
  sessionId: string | null;
  open: boolean;
  onClose: () => void;
}

export const ModalSummary = ({
  sessionId,
  open,
  onClose
}: ModalSummaryProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { currentUser } = useContext(UserContext);

  const [activeStep, setActiveStep] = useState(0);
  const [summary, setSummary] = useState("");

  const steps = [
    t("chatbot.summary.steps.processing_request"),
    t("chatbot.common.summary")
  ];

  const currentDate = useMemo(() => {
    if (open) {
      return new Date();
    }
    return new Date();
  }, [open]);

  const {
    data,
    isLoading,
    isError
  } = useChatSummary(sessionId || "", getLngCode(currentUser.language), open);

  useEffect(() => {
    if (activeStep === 0 && !isLoading && !isError && data) {
      setSummary(data.summary);
      setActiveStep(activeStep + 1);
    }
  }, [activeStep, isLoading, isError, data]);

  const pdfTitle = useMemo(() => {
    const current_date_fmt = currentDate.toISOString();
    return `Siren_Summary_${current_date_fmt.replaceAll("-", "_").replaceAll(":", "_").replace("Z", "")}.pdf`;
  }, [currentDate]);

  const handleCopy = () => {
    navigator.clipboard.writeText(summary);
  };

  const handleOnClose = () => {
    queryClient.cancelQueries({
      queryKey: [
        "chatbot",
        "summary",
        sessionId,
        getLngCode(currentUser.language)
      ]
    });
    setActiveStep(0);
    setSummary("");
    return onClose();
  };

  const handleDownloadAsPDF = () => {
    const summaryPdf = new jsPDF("p", "px", "a4");
    summaryPdf.setFontSize(12);
    summaryPdf.text(summary, 50, 50, { maxWidth: 355, align: "justify" });
    if (summary.length > 0) {
      summaryPdf.save(pdfTitle);
    }
  };

  const enabledActions = useMemo(() => {
    return summary.length > 0;
  }, [summary]);

  return (
    <Modal
      open={open}
      onClose={handleOnClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      id="modal-summary"
      key="modalu-summary"
    >
      <Box sx={modalStyle}>
        <Typography id="modal-modal-title" variant="h6" component="h2">
          <Box className="w-full flex pt-2 pr-4 pl-4 ">
            <Box className="w-3/4">{t("chatbot.common.summary")}</Box>
            <Box className="w-1/4 text-right">
              <IconButton
                onClick={handleOnClose}
                style={{
                  color: theme.palette.error.main
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </Typography>
        <Typography id="modal-modal-description" sx={{ mt: 2 }}>
          <Stack spacing={2}>
            <Stepper
              alternativeLabel
              activeStep={activeStep}
              connector={<ColorlibConnector />}
            >
              {steps.map((label, index) => {
                const stepProps: { completed?: boolean; active?: boolean } = {};

                stepProps.active = index === activeStep;
                stepProps.completed = activeStep > index;

                return (
                  <Step key={label} {...stepProps}>
                    <StepLabel StepIconComponent={ColorlibStepIcon}>
                      {label}
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Stack>

          {activeStep === 0 && (
            <Box className="w-full" sx={{ height: "100%" }}>
              {isLoading && (
                <LinearProgress color="primary" sx={{ marginTop: "15%" }} />
              )}
            </Box>
          )}
          {activeStep === 1 && (
            <Box
              className="w-full mt-4 pt-4 overflow-y-scroll"
              sx={{ height: "50vh" }}
            >
              <TextField
                autoFocus
                required
                id="summary-content"
                label={t("chatbot.summary.summary_content")}
                value={summary}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setSummary(event.target.value);
                }}
                error={summary.length === 0}
                type="text"
                fullWidth
                variant="standard"
                multiline
              />
            </Box>
          )}
        </Typography>
        <DialogActions>
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            <Box sx={{ flex: "1 1 auto", marginLeft: 1 }} />
            {activeStep === 1 && (
              <React.Fragment>
                <Tooltip
                  title={t("chatbot.common.download_as_pdf")}
                  aria-label={t("chatbot.common.download_as_pdf")}
                >
                  <IconButton
                    onClick={handleDownloadAsPDF}
                    disabled={!enabledActions}
                  >
                    <PictureAsPdfIcon color="error" />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={t("chatbot.common.copy_clipboard")}
                  aria-label={t("chatbot.common.copy_clipboard")}
                >
                  <IconButton onClick={handleCopy} disabled={!enabledActions}>
                    <ContentCopyIcon color="primary" />
                  </IconButton>
                </Tooltip>
              </React.Fragment>
            )}
          </Box>
        </DialogActions>
      </Box>
    </Modal>
  );
};
